import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGachaTickets } from 'api/gacha-tickets';
import { queryKeys } from '../../../../constants';
import { useUserStore } from 'store';
import { validateGamePlayManyTime } from 'utils';
import { EGameType, TAddGame, TConfirmGame, TProjectFemale } from 'types';
import { GachaTicketWallet } from 'types/gacha-tickets';
import { groupTickets } from 'pages/MyPage/GachaTickets/wallet';

type Selections = NonNullable<TAddGame['gameGachaTickets']>;
const ticketLimit = ({ quantity, type }: TConfirmGame) =>
  Math.min(quantity || 0, validateGamePlayManyTime(type) ? Infinity : 1);
function gameTicketGroups(wallet?: GachaTicketWallet) {
  const now = Math.max(Date.now(), Date.parse(wallet?.serverNow || '') || 0);
  return groupTickets(
    wallet?.usable.filter(
      (t) =>
        t.ticketType === 'GAME' &&
        t.status === 'UNUSED' &&
        !t.usedAt &&
        Date.parse(t.expiresAt) > now,
    ) || [],
  );
}
export function hasInvalidGameTickets(
  value: Selections,
  wallet?: GachaTicketWallet,
) {
  return value.some((s) => {
    const t = wallet?.usable.find((t) => t.ticketId === s.ticketId);
    return (
      !t ||
      t.ticketType !== 'GAME' ||
      t.gameId !== s.gameId ||
      t.status !== 'UNUSED' ||
      !!t.usedAt ||
      Date.parse(t.expiresAt) <=
        Math.max(Date.now(), Date.parse(wallet?.serverNow || '') || 0)
    );
  });
}
export function validGameTickets(
  value: Selections,
  games: TConfirmGame[],
  females: TProjectFemale[],
  wallet?: GachaTicketWallet,
  reassign = false,
): Selections {
  const counts = new Map<string, number>();
  const tickets = new Set<number>();
  const valid = value.filter((selection) => {
    const game = games.find((g) => g.id === selection.gameId && !g.disabled);
    const female = females.find((f) => f.user.id === selection.femaleId);
    const ticket = wallet?.usable.find(
      (t) => t.ticketId === selection.ticketId,
    );
    const key = `${selection.gameId}:${selection.femaleId}`;
    const count = (counts.get(key) || 0) + 1;
    if (
      !game ||
      !female ||
      !ticket ||
      hasInvalidGameTickets([selection], wallet) ||
      tickets.has(ticket.ticketId) ||
      count > ticketLimit(game) ||
      female.specialGameType === EGameType.GROUP_KING ||
      (game.type === EGameType.GROUP_SPECIAL &&
        female.specialGameType === EGameType.GROUP_SPECIAL)
    )
      return false;
    counts.set(key, count);
    tickets.add(ticket.ticketId);
    return true;
  });
  if (!reassign || hasInvalidGameTickets(value, wallet)) return valid;
  const pools = new Map(
    gameTicketGroups(wallet).map((g) => [g.first.gameId, g.items]),
  );
  return females.flatMap((female) =>
    games.flatMap(
      (game) =>
        pools
          .get(game.id)
          ?.splice(0, counts.get(`${game.id}:${female.user.id}`) || 0)
          .map((t) => ({
            ticketId: t.ticketId,
            gameId: game.id,
            femaleId: female.user.id,
          })) || [],
    ),
  );
}

export default function GameTicketSelection({
  games,
  females,
  value,
  onChange,
  disabled,
}: {
  games: TConfirmGame[];
  females: TProjectFemale[];
  value: Selections;
  onChange: (value: Selections) => void;
  disabled: boolean;
}) {
  const userId = useUserStore((state) => state.user?.id);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [queryKeys.SALON_GACHA_TICKETS, userId],
    queryFn: ({ signal }) => getGachaTickets(signal),
    enabled: !!userId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const selected = validGameTickets(value, games, females, data, true);
  const groups = gameTicketGroups(data);
  useEffect(() => {
    if (
      !hasInvalidGameTickets(value, data) &&
      JSON.stringify(value) !== JSON.stringify(selected)
    ) {
      onChange(selected);
    }
  }, [value, selected, data, onChange]);
  const rows = games.flatMap((game) => {
    const pool = groups.find((g) => g.first.gameId === game.id)?.items || [];
    const first = pool[0];
    if (!first) return [];
    const eligible = females.filter(
      (female) =>
        validGameTickets(
          [
            {
              ticketId: first.ticketId,
              gameId: game.id,
              femaleId: female.user.id,
            },
          ],
          games,
          [female],
          data,
        ).length,
    );
    return eligible.length
      ? [
          {
            game,
            pool,
            eligible,
            cap: Math.min(pool.length, ticketLimit(game) * eligible.length),
          },
        ]
      : [];
  });
  const countOf = (gameId: number) =>
    selected.filter((s) => s.gameId === gameId).length;
  const change = (gameId: number, delta: number) => {
    const pools = new Map(groups.map((g) => [g.first.gameId, [...g.items]]));
    const share = new Map<string, number>();
    rows.forEach((row) => {
      if (row.game.id !== gameId) {
        row.eligible.forEach((female) => {
          share.set(
            `${row.game.id}:${female.user.id}`,
            selected.filter(
              (s) =>
                s.gameId === row.game.id && s.femaleId === female.user.id,
            ).length,
          );
        });
        return;
      }
      let left = Math.min(
        row.cap,
        Math.max(0, countOf(row.game.id) + delta),
      );
      row.eligible.forEach((female) => {
        const take = Math.min(left, ticketLimit(row.game));
        left -= take;
        share.set(`${row.game.id}:${female.user.id}`, take);
      });
    });
    onChange(
      females.flatMap((female) =>
        games.flatMap(
          (game) =>
            pools
              .get(game.id)
              ?.splice(0, share.get(`${game.id}:${female.user.id}`) || 0)
              .map((t) => ({
                ticketId: t.ticketId,
                gameId: game.id,
                femaleId: female.user.id,
              })) || [],
        ),
      ),
    );
  };
  return (
    <section
      aria-label="ゲーム券"
      className="mt-4 border-y border-neutral-200 bg-white py-4"
    >
      <p>ゲーム券を使う</p>
      {isPending ? (
        <p role="status">券を確認しています</p>
      ) : isError ? (
        <button type="button" onClick={() => refetch()}>
          券を取り直す
        </button>
      ) : !groups.length ? (
        <p className="text-[#756e64]">お持ちのゲーム券はありません</p>
      ) : !rows.length ? (
        <p>このゲームに使える券はありません</p>
      ) : (
        rows.map((row) => {
          const label = row.game.name;
          const count = countOf(row.game.id);
          return (
            <div
              key={row.game.id}
              role="group"
              aria-label={label}
              className="mt-3 flex items-center gap-2 border-t border-neutral-200 py-3"
            >
              <div className="min-w-0 flex-1">
                <p>{label}</p>
                <p className="text-xs text-[#756e64]">
                  お持ちの券 {row.pool.length} 枚
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  aria-label={`${label}の券を減らす`}
                  className="flex h-[44px] w-[44px] shrink-0 items-center justify-center disabled:opacity-40"
                  disabled={disabled || !count}
                  onClick={() => change(row.game.id, -1)}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-[#4a453e] leading-none"
                  >
                    −
                  </span>
                </button>
                <span
                  aria-live="polite"
                  className={`w-[34px] text-center font-bold${
                    count ? ' text-primary-700' : ''
                  }`}
                >
                  {count}枚
                </span>
                <button
                  type="button"
                  aria-label={`${label}の券を増やす`}
                  className="flex h-[44px] w-[44px] shrink-0 items-center justify-center disabled:opacity-40"
                  disabled={disabled || count >= row.cap}
                  onClick={() => change(row.game.id, 1)}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-[#4a453e] leading-none"
                  >
                    ＋
                  </span>
                </button>
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}
